import * as Scrivito from 'scrivito'

import { getInstanceIdFromUrl } from './getInstanceIdFromUrl'
import { isRuntimeEnv } from './isRuntimeEnv'

/**
 * the default JR API URL
 */
export const JR_API_URL = 'https://api.justrelate.com'

/**
 * the beta JR API URL
 */
export const JR_BETA_API_URL = 'https://api.justrelate.com/beta'

/**
 * (pseudo) ID of the global instance
 */
export const PLATFORM_GLOBAL_INSTANCE_ID = '00000000000000000000000000000000'

type ApiClient = ReturnType<typeof Scrivito.createRestApiClient>

/**
 * type of API fetch options
 */
export type ApiFetchOptions = Parameters<ApiClient['fetch']>[1]

/** A fetch cache to avoid redundant backend requests. Anything but GET clears the whole cache. */
const apiFetchGetCache = new Map<string, Promise<unknown>>()

/**
 * performs an API fetch
 * @param path the resource path
 * @param options fetch options
 * @returns a promise providing the fetch result
 */
export async function apiFetch<T = unknown>(
  path: string,
  options?: ApiFetchOptions,
): Promise<T> {
  // prepare fetch options
  const fetchOptions: ApiFetchOptions = { method: 'get', ...options }
  fetchOptions.audience = JR_API_URL
  fetchOptions.authViaInstance =
    options?.authViaInstance ||
    getInstanceIdFromUrl() ||
    PLATFORM_GLOBAL_INSTANCE_ID
  // prepare cache handling
  const is_get = fetchOptions.method?.toLowerCase() === 'get'
  if (!is_get) {
    // no "GET" --> clear cache
    apiFetchGetCache.clear()
  }
  // check the cache
  const cacheKey = JSON.stringify({ path, fetchOptions })
  const cachedPromise = apiFetchGetCache.get(cacheKey)
  if (cachedPromise) {
    // cache hit!
    return cachedPromise as Promise<T>
  }
  // fetch request not cached
  const promise = getApiClient().fetch(path, fetchOptions)
  if (is_get) {
    // cache the result
    apiFetchGetCache.set(cacheKey, promise)
  }
  // done
  return promise as Promise<T>
}

/** lazily initialized API client */
let lazyApiClient: undefined | ApiClient

/**
 * gets the API client
 * @returns the API client
 */
function getApiClient(): ApiClient {
  if (!lazyApiClient) {
    lazyApiClient = Scrivito.createRestApiClient(getJrBaseUrl())
  }
  return lazyApiClient
}

/**
 * retrieves the JR API URL
 * @returns the JRI API URL to be used
 */
export function getJrBaseUrl(): string {
  if (isRuntimeEnv('beta', 'dev')) {
    return JR_BETA_API_URL
  }
  return JR_API_URL
}

export async function getIamToken(tenant: string): Promise<string> {
  const authTokenResp = await fetch(
    `/auth/instance/${tenant}/token?audience=${JR_API_URL}`,
  )
  const tokenRes = await authTokenResp.json()
  return tokenRes.access_token
}
